/**
 * iOS-safe AudioContext bootstrap + tiny synthesised SFX.
 *
 * Spec requirement covered here:
 *   4. iOS audio — Safari/Chrome on iOS won't allow audio playback until an
 *      AudioContext is created AND .resume()d inside a user gesture. We hook
 *      the very first pointerdown on window with {once: true}; subsequent
 *      gestures are no-ops because audioCtx is already alive.
 *
 * The three SFX (kick / goal / miss) are synthesised at runtime so we don't
 * ship audio files. Real .mp3/.ogg assets can replace these by swapping
 * the bodies of playKick / playGoal / playMiss; the public surface stays the
 * same and the unlock logic does not need to change.
 */

let audioCtx: AudioContext | null = null;

/**
 * Idempotent. Safe to call from anywhere; the first call inside a user
 * gesture is the one that satisfies iOS's autoplay policy.
 */
function ensureAudio(): AudioContext | null {
  if (audioCtx) return audioCtx;
  type WindowWithWebkitAudio = Window & {
    webkitAudioContext?: typeof AudioContext;
  };
  const w = window as WindowWithWebkitAudio;
  const Ctor: typeof AudioContext | undefined = window.AudioContext ?? w.webkitAudioContext;
  if (!Ctor) return null;
  audioCtx = new Ctor();
  // resume() on iOS is required even immediately after construction.
  void audioCtx.resume();
  return audioCtx;
}

/**
 * Install the unlock listener exactly once. Idempotent — safe to call on
 * every component mount; only the first invocation registers the listener.
 *
 * Implementation note: per spec we use {once: true} so the listener removes
 * itself after firing. We still also expose ensureAudio() for the kick SFX
 * which fires on the same gesture.
 */
let unlockInstalled = false;
export function installAudioUnlock(): void {
  if (unlockInstalled) return;
  unlockInstalled = true;
  const handler = (): void => {
    ensureAudio();
  };
  window.addEventListener("pointerdown", handler, { once: true });
}

// ----- placeholder SFX -----
// Each plays a short envelope-shaped oscillator. Replace freely.

function beep(opts: {
  freqStart: number;
  freqEnd: number;
  durationMs: number;
  type?: OscillatorType;
  gain?: number;
}): void {
  const ctx = ensureAudio();
  if (!ctx) return;
  const { freqStart, freqEnd, durationMs, type = "sine", gain = 0.18 } = opts;
  const t0 = ctx.currentTime;
  const t1 = t0 + durationMs / 1000;

  const osc = ctx.createOscillator();
  osc.type = type;
  osc.frequency.setValueAtTime(freqStart, t0);
  osc.frequency.exponentialRampToValueAtTime(Math.max(1, freqEnd), t1);

  const env = ctx.createGain();
  env.gain.setValueAtTime(0, t0);
  env.gain.linearRampToValueAtTime(gain, t0 + 0.01);
  env.gain.exponentialRampToValueAtTime(0.0001, t1);

  osc.connect(env).connect(ctx.destination);
  osc.start(t0);
  osc.stop(t1 + 0.02);
}

/** Low thunk on contact. */
export function playKick(): void {
  beep({ freqStart: 220, freqEnd: 80, durationMs: 140, type: "triangle", gain: 0.22 });
}

/** Rising chord on goal. */
export function playGoal(): void {
  beep({ freqStart: 440, freqEnd: 880, durationMs: 220, type: "sawtooth", gain: 0.18 });
  setTimeout(() => beep({ freqStart: 660, freqEnd: 1320, durationMs: 240, type: "sawtooth", gain: 0.14 }), 80);
}

/** Descending sigh on miss / save. */
export function playMiss(): void {
  beep({ freqStart: 300, freqEnd: 110, durationMs: 280, type: "sine", gain: 0.16 });
}
